import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiIcon,
  MiIconButton,
  MiLoader,
} from "@miplatform/components";
import { EMAIL_ENTITY_TYPE } from "@miplatform/constants";
import {
  ButtonVariants,
  EmailTemplateType,
  IAttempt,
  IStep,
  LoaderType,
  StepStatus,
} from "@miplatform/types";
import { mdiArrowULeftTop } from "@mdi/js";
import { EmailForm, useEmailForm } from "@mibuildweb/components";
import { Box, Typography } from "@mui/material";
import { useWorkOrderStepCardViewModel } from "./useWorkOrderStepCardViewModel";

export const WorkOrderEmailStepCard = ({
  step,
  number,
  isEditMode,
  isLoading,
  isDisabled,
  isUndoDisabled,
  attempt,
  onRefresh,
}: WorkOrderEmailStepCardProps) => {
  const wasCompleted = step.status === StepStatus.Completed;
  const isCompleted =
    step.status === StepStatus.Completed ||
    step.stepResult?.status === StepStatus.Completed;
  const shouldShowEmailForm = !isCompleted || isEditMode;
  const { theme, classes } = useStyles({ isCompleted });
  const tempOrder = useMemo(
    () => ({ ...attempt, id: attempt.orderId }),
    [attempt]
  );
  const emailFormViewModel = useEmailForm({
    entity: tempOrder,
    entityType: EMAIL_ENTITY_TYPE.ORDER,
    step,
    attempt,
    templateType: step.templateType || EmailTemplateType.RoughBuilderSandDayOne,
    onClose: onRefresh,
  });
  const { isLoading: loading, undoStepResult } = useWorkOrderStepCardViewModel({
    attempt,
    step,
    onRefresh,
  });

  return (
    <Box className={classes.section}>
      <MiLoader
        type={LoaderType.Linear}
        isLoading={loading}
        style={{ position: "absolute", top: 0, left: 0, right: 0 }}
      />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        pb={2}
      >
        <Box display="flex" flexDirection="column">
          {step.isRequired && (
            <Typography className={classes.required}>REQUIRED</Typography>
          )}
          <Typography
            className={classes.title}
          >{`${number}. ${step.title}`}</Typography>
          <Typography className={classes.description}>
            {step.description}
          </Typography>
        </Box>
        <Box display="flex" pl={1} gap={1}>
          {isCompleted && !isEditMode ? (
            <MiButton variant={ButtonVariants.Text} title="Completed" />
          ) : (
            <MiButton
              title="Send Email"
              variant={ButtonVariants.Contained}
              disabled={
                emailFormViewModel.isSubmitDisabled || isDisabled || isLoading
              }
              onClick={emailFormViewModel.sendEmail}
            />
          )}
          {isEditMode && (
            <MiIconButton
              disabled={
                isLoading ||
                isDisabled ||
                !isCompleted ||
                isUndoDisabled ||
                wasCompleted
              }
              onClick={undoStepResult}
            >
              <MiIcon path={mdiArrowULeftTop} />
            </MiIconButton>
          )}
        </Box>
      </Box>
      {!isDisabled && shouldShowEmailForm && (
        <Box flexGrow={1} pb={2}>
          <EmailForm
            showHeader
            entityType={EMAIL_ENTITY_TYPE.ORDER}
            viewModel={emailFormViewModel}
            containerStyle={{
              borderRadius: theme.spacing(1),
              border: `1px solid ${theme.palette.grey.accent}`,
            }}
            formContainerStyle={{
              backgroundColor: theme.palette.grey.light,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

interface WorkOrderEmailStepCardProps {
  step: IStep;
  isDisabled?: boolean;
  isUndoDisabled?: boolean;
  isLoading?: boolean;
  number: number;
  attempt: IAttempt;
  isEditMode?: boolean;
  onRefresh: () => void;
}

const useStyles = makeStyles<{ isCompleted: boolean }>()(
  (theme, { isCompleted }) => ({
    section: {
      display: "flex",
      flexDirection: "column",
      position: "relative",
      padding: theme.spacing(3),
      width: "100%",
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.grey.light,
      border: `1px solid ${
        isCompleted ? theme.palette.success.main : theme.palette.grey.accent
      }`,
    },
    required: {
      fontSize: theme.typography.pxToRem(10),
      color: theme.palette.error.main,
    },
    title: {
      fontWeight: theme.fontWeight.semiBold,
    },
    description: {
      color: theme.palette.grey.dark,
      fontSize: theme.typography.pxToRem(14),
    },
  })
);
